<template>
    <main-container title="Prototype">
        <div class="form-group">
            <label>Tire Size:</label>
            <div class="input-group">
                <input
                    id="width"
                    v-model="width"
                    type="text"
                    class="form-control"
                >
                <input
                    type="text"
                    value="/"
                    disabled
                    class="col-1 text-center"
                >
                <input
                    id="aspectRatio"
                    v-model="aspectRatio"
                    type="text"
                    class="form-control"
                >
                <input
                    type="text"
                    value="R"
                    disabled
                    class="col-1 text-center"
                >
                <input
                    id="diameter"
                    v-model="diameter"
                    type="text"
                    class="form-control"
                >
            </div>
        </div>

        <div class="row">
            <div class="form-group col">
                <label
                    for="load-index"
                    class="control-label"
                >Load Index</label>
                <input
                    id="load-index"
                    v-model="loadIndex"
                    type="text"
                    class="form-control"
                >
            </div>
            <div class="form-group col">
                <label
                    for="axle-weight"
                    class="control-label"
                >Axle Weight</label>
                <input
                    id="axle-weight"
                    v-model="axleWeight"
                    type="text"
                    class="form-control"
                >
            </div>
        </div>

        <div class="row">
            <div class="form-group col">
                <label
                    for="tire-type"
                    class="control-label"
                >Tire Type</label>
                <select
                    id="tire-type"
                    v-model="type"
                    class="form-control"
                >
                    <option
                        value=""
                        disabled
                    >
                        Select Tire Type
                    </option>
                    <option
                        v-for="option in typeOptions"
                        :key="option.value"
                        :value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>
            <div class="form-group col">
                <label
                    for="application"
                    class="control-label"
                >Application</label>
                <select
                    id="application"
                    v-model="application"
                    class="form-control"
                >
                    <option
                        value=""
                        disabled
                    >
                        Select Application
                    </option>
                    <optgroup
                        v-for="app in applicationOptions"
                        :key="app.text"
                        :label="app.text"
                    >
                        <option
                            v-for="option in app.options"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.text }}
                        </option>
                    </optgroup>
                </select>
            </div>
            <div class="form-group col">
                <label
                    for="quantity"
                    class="control-label"
                >Tires</label>
                <select
                    id="quantity"
                    v-model="quantity"
                    class="form-control"
                >
                    <option value="1">
                        Single
                    </option>
                    <option value="2">
                        Duals
                    </option>
                    <option value="3">
                        Triples
                    </option>
                </select>
            </div>
        </div>

        <div v-if="showResults">
            <div class="row">
                <h2 class="col mb-1">
                    PSI: {{ psi }}
                </h2>
                <h2 class="col mb-1">
                    KPA: {{ kpa }}
                </h2>
            </div>
            <div class="row">
                <p class="col">
                    Max Weight:
                    <b>
                        {{ lbs }} Lb /
                        {{ kg }} Kg
                    </b>
                </p>
            </div>
            <table
                v-if="showResults"
                class="w-100 table table-sm table-striped table-hover"
            >
                <tr>
                    <th>Load Index</th>
                    <th>KPA</th>
                    <th>PSI</th>
                    <th>LBS</th>
                </tr>

                <tr
                    v-for="d in options"
                    :key="d.kpa"
                    :class="rowClass(d)"
                >
                    <td>{{ d.loadIndex }}</td>
                    <td>{{ d.kpa }}</td>
                    <td>{{ d.psi }}</td>
                    <td>{{ d.lbs }}</td>
                </tr>
            </table>
        </div>
    </main-container>
</template>

<script>
import _ from 'lodash'
import MainContainer from '@/components/main-container'

import { calculate, getOptions, lbs2kg, roundTo } from '@vervocity/tire-pressure-calculations'
import { applicationOptions, typeOptions } from '@/helpers/options'

export default {
    components: {
        MainContainer,
    },
    data: () => ({
        // baseType: '',
        // type: '',
        // application: '',
        // width: null,
        // aspectRatio: null,
        // diameter: null,
        // loadIndex: null,
        // quantity: 1,
        // axleWeight: null,

        // LSW420/80R30
        baseType: 'LSW/IF/CFO',
        type: 'LSW/IF/CFO',
        application: '20-grain-cart',
        width: 420,
        aspectRatio: 80,
        diameter: 30,
        loadIndex: 300,
        quantity: 1,
        axleWeight: 10000,

        // baseType: 'VF',
        // type: 'VF',
        // application: 'all',
        // width: 1100,
        // aspectRatio: 45,
        // diameter: 46,
        // loadIndex: 195,
        // quantity: 1,
        // axleWeight: 53000,
    }),

    computed: {
        tire () {
            return {
                baseType: this.baseType,
                type: this.type,
                application: this.application,
                width: this.width,
                aspectRatio: this.aspectRatio,
                diameter: this.diameter,
                loadIndex: this.loadIndex,
                axleWeight: this.axleWeight,
                quantity: this.quantity,
            }
        },
        typeOptions: () => typeOptions,
        applicationOptions () {
            return _.filter(applicationOptions, item => this.baseType === item.type)
        },
        canCalculate () {
            return _.every(this.tire, item => _.chain(item).toString().trim().value() !== '')
        },
        hasValidCalculations () {
            return _.toSafeInteger(this.psi) > 0
        },
        showResults () {
            return this.canCalculate && this.hasValidCalculations
        },
        options () {
            return getOptions(this.tire)
        },
        data () {
            return calculate(this.tire)
        },
        psi () {
            return _.get(this.data, 'psi', 0)
        },
        kpa () {
            return _.get(this.data, 'kpa', 0)
        },
        lbs () {
            return _.get(this.data, 'lbs', 0)
        },
        kg () {
            return roundTo(lbs2kg(this.lbs), 5)
        },
    },
    watch: {
        type (value) {
            this.baseType = _.chain(this.typeOptions).find({ value }).get('base').value()
        },
        baseType (value) {
            const options = _.chain(this.applicationOptions)
                .map(item => {
                    return _.chain(item).get('options').map('value').value()
                })
                .flatten()
                .value()
            if (!_.includes(options, this.application)) {
                this.application = ''
            }
        },
    },
    methods: {
        rowClass (data) {
            return {
                'table-success': data.psi === this.psi,
                'text-muted': data.psi !== this.psi,
            }
        },
    },
}
</script>
